// src/services/authService.ts
import axios from 'axios';

interface User {
  id: number;
  email: string;
  name: string;
  headOfficeId?: number;
  role: string;
  status?: string;
  authzVersion?: number;
  autoLogoutMinutes?: number | null;
}

interface AuthResponse {
  token: string;
  user: User;
  permissions: Record<number, string[]>; // storeId -> permission codes
  headOfficePermissions: string[];
  storeAccess?: Array<{ storeId: number; storeName: string; isDefault: number | boolean }>;
}

export const LAST_ACTIVITY_KEY = 'lastActivityAt';

class AuthService {
  private serverUrl: string;
  private token: string | null = null;
  private currentUser: User | null = null;
  private permissions: Record<number, string[]> = {};
  private headOfficePermissions: string[] = [];
  private storeAccess: Array<{ storeId: number; storeName: string; isDefault: number | boolean }> = [];
  private permissionValidated: boolean = false;

  constructor() {
    
    this.serverUrl = process.env.REACT_APP_SERVER_URL || 'https://crmbackend.enrichpos.com.au';
    this.initFromStorage();
    this.setupTokenInterceptors();
  }

  // 后端在 X-Refreshed-Token 返回续期后的 token；token 过期/失效（401）时登出并回到登录页
  private setupTokenInterceptors() {
    axios.interceptors.response.use(
      (response) => {
        this.applyRefreshedToken(response.headers?.['x-refreshed-token']);
        return response;
      },
      (error) => {
        const url = String(error?.config?.url || '');
        if (error?.response?.status === 401 && this.token && !url.includes('/auth/login')) {
          this.handleSessionExpired();
        }
        return Promise.reject(error);
      },
    );
  }

  applyRefreshedToken(token?: string | null): void {
    if (!token || !this.token || token === this.token) return;
    this.token = token;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  handleSessionExpired(): void {
    this.logout();
    if (window.location.pathname !== '/login') window.location.replace('/login');
  }

  private initFromStorage() {
    this.token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const permissionsStr = localStorage.getItem('permissions');
    const headOfficePermissionsStr = localStorage.getItem('headOfficePermissions');
    const storeAccessStr = localStorage.getItem('storeAccess');
    
    if (userStr) this.currentUser = JSON.parse(userStr);
    if (permissionsStr) {
      this.permissions = JSON.parse(permissionsStr);
      this.permissionValidated = false; // 重新加载时需要重新验证
    }
    if (headOfficePermissionsStr) {
      this.headOfficePermissions = JSON.parse(headOfficePermissionsStr);
    }
    if (storeAccessStr) this.storeAccess = JSON.parse(storeAccessStr);

    if (this.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    }
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    try {
   
      console.log('Logging in with email:', this.serverUrl);
      const response = await axios.post<AuthResponse>(`${this.serverUrl}/auth/login`, {
        email,
        password
      });

      const { token, user, permissions, headOfficePermissions = [], storeAccess = [] } = response.data;
      this.setSession(token, user, permissions, headOfficePermissions, storeAccess);
      this.permissionValidated = true; // 登录时的权限是已验证的
      
      return response.data;
    } catch (error) {
      console.error('AuthService: Login error:', error);
      throw error;
    }
  }

  private setSession(
    token: string, 
    user: User, 
    permissions: Record<number, string[]>,
    headOfficePermissions: string[] = [],
    storeAccess: Array<{ storeId: number; storeName: string; isDefault: number | boolean }> = []
  ): void {
    this.token = token;
    this.currentUser = user;
    this.permissions = permissions;
    this.headOfficePermissions = headOfficePermissions;
    this.storeAccess = storeAccess;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('permissions', JSON.stringify(permissions));
    localStorage.setItem('headOfficePermissions', JSON.stringify(headOfficePermissions));
    localStorage.setItem('storeAccess', JSON.stringify(storeAccess));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  logout(): void {
    this.token = null;
    this.currentUser = null;
    this.permissions = {};
    this.headOfficePermissions = [];
    this.storeAccess = [];
    this.permissionValidated = false;

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('headOfficePermissions');
    localStorage.removeItem('storeAccess');
    localStorage.removeItem('currentStoreId');
    localStorage.removeItem(LAST_ACTIVITY_KEY);

    delete axios.defaults.headers.common['Authorization'];
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  getUserRole(): string | null {
    return this.currentUser?.role || null;
  }

  getHeadOfficeId(): number | undefined {
    return this.currentUser?.headOfficeId;
  }

  getAccessibleHeadOffice(): { id: number; canEdit: boolean } | undefined {
    const headOfficeId = this.currentUser?.headOfficeId;
    if (!headOfficeId) return undefined;

    // 检查是否有总部编辑权限
    const hasEditPermission = this.currentUser?.role === 'admin' ||
      this.hasHeadOfficePermission('ho.settings.view');

    return {
      id: headOfficeId,
      canEdit: hasEditPermission,
    };
  }

  // 从缓存中检查权限（用于快速UI渲染）
  hasPermissionFromCache(permissionCode: string, storeId?: number): boolean {
    if (this.currentUser?.role === 'admin') return true;
    const targetStoreId = storeId || Number(localStorage.getItem('currentStoreId'));
    if (!targetStoreId) return false;
    
    const storePermissions = this.permissions[targetStoreId] || [];
    return storePermissions.includes(permissionCode);
  }

  // 从服务器验证权限（用于重要操作）
  async validatePermissionWithServer(permissionCode: string, storeId?: number): Promise<boolean> {
    try {
      const targetStoreId = storeId || Number(localStorage.getItem('currentStoreId'));
      if (!targetStoreId) return false;

      const response = await axios.post(`${this.serverUrl}/auth/validate-permission`, {
        permissionCode,
        storeId: targetStoreId
      });

      return response.data.hasPermission;
    } catch (error) {
      console.error('权限验证失败:', error);
      return false;
    }
  }

  // 刷新权限缓存
  async refreshPermissions(): Promise<void> {
    try {
      const response = await axios.get<{
        permissions: Record<number, string[]>;
        headOfficePermissions: string[];
      }>(
        `${this.serverUrl}/auth/permissions`
      );
      
      this.permissions = response.data.permissions;
      this.headOfficePermissions = response.data.headOfficePermissions || [];
      this.permissionValidated = true;
      localStorage.setItem('permissions', JSON.stringify(this.permissions));
      localStorage.setItem('headOfficePermissions', JSON.stringify(this.headOfficePermissions));
    } catch (error) {
      console.error('刷新权限失败:', error);
      throw error;
    }
  }

  // 获取权限验证状态
  isPermissionValidated(): boolean {
    return this.permissionValidated;
  }

  // 检查用户是否有特定权限
  hasPermission(permissionCode: string): boolean {
    if (this.currentUser?.role === 'admin') return true;
    const storeId = localStorage.getItem('currentStoreId');
    if (!storeId) {
      return false;
    }
    const storeIdNum = Number(storeId);
    if (!this.permissions[storeIdNum]) {
      return false;
    }
    return this.permissions[storeIdNum].includes(permissionCode);
  }

  // 检查用户是否有任意一个权限
  hasAnyPermission(permissionCodes: string[]): boolean {
    return permissionCodes.some(code => this.hasPermission(code));
  }

  // 检查用户是否有所有权限
  hasAllPermissions(permissionCodes: string[]): boolean {
    return permissionCodes.every(code => this.hasPermission(code));
  }

  // 获取特定商店的所有权限
  getStorePermissions(storeId: number): string[] {
    return this.permissions[storeId] || [];
  }

  hasHeadOfficePermission(permissionCode: string): boolean {
    return this.currentUser?.role === 'admin' || this.headOfficePermissions.includes(permissionCode);
  }

  hasAnyHeadOfficePermission(permissionCodes: string[]): boolean {
    return this.currentUser?.role === 'admin' ||
      permissionCodes.some((code) => this.headOfficePermissions.includes(code));
  }

  getHeadOfficePermissions(): string[] {
    return [...this.headOfficePermissions];
  }

  // 获取用户可访问的所有商店ID
  getAccessibleStoreIds(): number[] {
    return [...new Set([
      ...Object.keys(this.permissions).map(Number),
      ...this.storeAccess.map((store) => Number(store.storeId)),
    ])];
  }

  // 检查用户是否可以访问特定商店
  canAccessStore(storeId: number): boolean {
    return !!this.permissions[storeId] || this.storeAccess.some((store) => Number(store.storeId) === storeId);
  }

  // 检查用户是否是管理员
  isAdmin(): boolean {
    return this.currentUser?.role === 'admin';
  }

  // 设置当前商店
  setCurrentStore(storeId: number | null): void {
    if (storeId === null) {
      localStorage.removeItem('currentStoreId');
    } else {
      localStorage.setItem('currentStoreId', storeId.toString());
    }
  }

  // 获取当前商店ID
  getCurrentStoreId(): number | null {
    const storeId = localStorage.getItem('currentStoreId');
    return storeId ? Number(storeId) : null;
  }

  async changePassword(currentPassword: string, newPassword: string) {
    try {
      const response = await axios.post(`${this.serverUrl}/auth/change-password`, {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('修改密码失败:', error);
      throw error;
    }
  }
}

export const authService = new AuthService();

export type {
  User,
  AuthResponse
};
